import {
  useQuery,
  QueryKey,
  QueryObserverOptions,
  UseQueryResult,
  MutationKey,
  useMutation,
  MutateOptions,
} from "react-query";

import {
  tenantListService,
  tenantDetailService,
  tenantCreateService,
  tenantUpdateService,
  tenantListShortService,
} from "./services";

type TenantListResponse = Awaited<ReturnType<typeof tenantListService>>;
type TenantShortListResponse = Awaited<
  ReturnType<typeof tenantListShortService>
>;
type TenantDetailResponse = Awaited<ReturnType<typeof tenantDetailService>>;

export const useTenants = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof tenantListService>[0];
  options?: QueryObserverOptions<unknown, unknown, TenantListResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "tenantListHooks",
    queryFn: () => tenantListService(params),
    ...options,
  });

  return response as UseQueryResult<TenantListResponse>;
};

export const useShortTenants = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof tenantListShortService>[0];
  options?: QueryObserverOptions<unknown, unknown, TenantShortListResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "tenantShortListHooks",
    queryFn: () => tenantListShortService(params),
    ...options,
  });

  return response as UseQueryResult<TenantShortListResponse>;
};

export const useTenantDetail = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof tenantDetailService>[0];
  options?: QueryObserverOptions<unknown, unknown, TenantDetailResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "tenantDetailHooks",
    queryFn: () => tenantDetailService(params),
    ...options,
  });

  return response as UseQueryResult<TenantDetailResponse>;
};

export const useTenantUpdate = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    TenantDetailResponse,
    unknown,
    Parameters<typeof tenantUpdateService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "tenantUpdateHooks",
    mutationFn: tenantUpdateService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useTenantCreate = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    TenantDetailResponse,
    unknown,
    Parameters<typeof tenantCreateService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "tenantCreateHooks",
    mutationFn: tenantCreateService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};
